import { LiveAnnouncer } from "@angular/cdk/a11y";
import { BreakpointObserver } from "@angular/cdk/layout";
import { Inject, Injectable, Injector, Optional, SkipSelf } from "@angular/core";
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef
} from "@angular/material/snack-bar";
import { DynamicOverlay } from "../dynamic-overlay/dynamic-overlay";
import { SnackBarComponent } from "./snack-bar.component";

type SnackBarType = "info" | "success" | "error";

/* eslint-disable jsdoc/no-types */
@Injectable({ providedIn: "root" })
export class DynamicSnackBar extends MatSnackBar {
  private readonly dynamicOverlay: DynamicOverlay;
  private readonly defaultConfig: MatSnackBarConfig;

  constructor(
    overlay: DynamicOverlay,
    live: LiveAnnouncer,
    injector: Injector,
    breakpointObserver: BreakpointObserver,
    @Optional() @SkipSelf() parentSnackBar: MatSnackBar,
    @Inject(MAT_SNACK_BAR_DEFAULT_OPTIONS) defaultConfig: MatSnackBarConfig
  ) {
    super(overlay, live, injector, breakpointObserver, parentSnackBar, defaultConfig);

    this.dynamicOverlay = overlay;
    this.defaultConfig = defaultConfig;
  }

  /** @override Default materail open method */
  public open(
    message: string,
    action: string = "",
    config: MatSnackBarConfig = {},
    type: SnackBarType = "info",
    isMain: boolean = true
  ): MatSnackBarRef<SnackBarComponent> {
    /** Render snack bar in main overlay container */
    this.dynamicOverlay.setMainOverlay(isMain);

    let _panelClass: Array<string> = [];

    if (config.panelClass) {
      if (Array.isArray(config.panelClass)) {
        _panelClass = config.panelClass;
      } else {
        _panelClass = [config.panelClass];
      }
    }

    const _config: MatSnackBarConfig = {
      ...this.defaultConfig,
      ...config,
      panelClass: ["ene-snack-bar", type, ..._panelClass]
    };

    /**
     * Since the user doesn't have access to the component, we can
     * override the data to pass in our own message and action.
     */
    _config.data = { message, action };

    if (!_config.announcementMessage) {
      _config.announcementMessage = message;
    }

    return this.openFromComponent(SnackBarComponent, _config);
  }

  public info(
    message: string,
    config?: MatSnackBarConfig,
    action?: string,
    isMain?: boolean
  ): MatSnackBarRef<SnackBarComponent> {
    return this.open(message, action, config, "info", isMain);
  }

  public error(
    message: string,
    config?: MatSnackBarConfig,
    action?: string,
    isMain?: boolean
  ): MatSnackBarRef<SnackBarComponent> {
    return this.open(message, action, config, "error", isMain);
  }

  public success(
    message: string,
    config?: MatSnackBarConfig,
    action?: string,
    isMain?: boolean
  ): MatSnackBarRef<SnackBarComponent> {
    return this.open(message, action, config, "success", isMain);
  }
}
